import React, { useState, useEffect } from "react";
import FaqSection from "../components/FaqSection";
import { Helmet } from "react-helmet-async";
import zohoStyles from "./ZohoFinal.module.css";
import Navigation from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ArrowRight,
  Building2,
  Shield,
  Network,
  Users2,
  PieChart,
  Package,
  PlugZap,
  Plus,
  Minus,
  Phone,
  Mail,
} from "lucide-react";

/* ✅ Assets */
import OracleLogo from "../assests/images/EBS.png";
import Client1 from "../assests/images/Suguna.png";
import Client2 from "../assests/images/integra.svg";

const siteUrl = "https://www.cloudare.in/services-ebs";
const calendly = "https://calendly.com/cloudare";
const email = "sales@cloudare.in";
const phoneDisplay = "+1 628-282-5683";
const phoneHref = "+16282825683";

const faqList = [
  {
    q: "What versions of Oracle E-Business Suite (EBS) do you support?",
    a: "We support Oracle E-Business Suite R11i, R12, and R12.2 including online patching, upgrades, and long-term AMS support.",
  },
  {
    q: "Oracle EBS implementation and upgrade services in India",
    a: "We provide Oracle EBS fresh implementations, version upgrades, R12.2 migrations, and post-upgrade stabilization services across India.",
  },
  {
    q: "Do you provide Oracle EBS AMS and production support?",
    a: "Yes — we offer L2/L3 AMS support, issue resolution, performance tuning, monitoring, and SLA-driven support models.",
  },
  {
    q: "Oracle EBS support and maintenance services after Premier Support",
    a: "We provide extended support for Oracle EBS after Premier Support, including security fixes, compliance updates, and functional enhancements.",
  },
  {
    q: "Can Oracle EBS be integrated with Oracle Fusion or cloud applications?",
    a: "Yes — we integrate Oracle EBS with Oracle Fusion, OIC, Salesforce, banking systems, and third-party cloud platforms.",
  },
  {
    q: "Oracle EBS integration services using OIC, SOA, and APIs",
    a: "We build robust integrations using Oracle OIC, SOA Suite, REST/SOAP APIs, database links, and flat-file interfaces.",
  },
  {
    q: "Do you support GST, e-Invoicing, and India Localization in Oracle EBS?",
    a: "Yes — we implement and support India Localization, GST, TDS, e-Invoicing, e-Way Bill, and statutory reporting in Oracle EBS.",
  },
  {
    q: "Oracle EBS Financials, SCM, and Manufacturing module support",
    a: "We support Oracle EBS Financials, SCM, Manufacturing, Procurement, Projects, and Inventory modules.",
  },
  {
    q: "Oracle EBS performance tuning and database optimization services",
    a: "Our services include SQL tuning, concurrent program optimization, workflow tuning, and Oracle Database performance improvements.",
  },
  {
    q: "Oracle EBS technical services – Forms, Reports, OAF, and PL/SQL",
    a: "We provide EBS technical development using Oracle Forms, Reports, OAF, BI Publisher, PL/SQL, and workflows.",
  },
  {
    q: "Oracle EBS data migration and master data management services",
    a: "We handle data migration, validation, reconciliation, and master data management for Oracle EBS implementations and upgrades.",
  },
  {
    q: "Oracle EBS cloud strategy – coexistence or migration to Oracle Fusion",
    a: "We help define cloud roadmaps including EBS–Fusion coexistence, phased migration, and full cloud transformation strategies.",
  },
  {
    q: "Oracle EBS services in Raipur, Chhattisgarh",
    a: "We provide Oracle E-Business Suite implementation, upgrade, and AMS support services in Raipur, Chhattisgarh, and across India.",
  },
  {
    q: "Oracle EBS consultants in India for long-term support",
    a: "Our experienced Oracle EBS consultants deliver reliable long-term support for enterprises across manufacturing, BFSI, healthcare, and public sectors.",
  },
  {
    q: "Oracle EBS security, compliance, and audit support services",
    a: "We support role-based access control, audit trails, compliance reporting, and security best practices for Oracle EBS.",
  },
];

export default function OracleEbsServicesPage() {
  const [activeIndex, setActiveIndex] = useState(null);

  return (
    <main className={zohoStyles.main}>
      <Navigation />

      <Helmet>
        <title>
          Oracle EBS Implementation & Support | Cloudare Technologies
        </title>
        <meta
          name="description"
          content="Oracle E-Business Suite implementation, upgrades, integrations, AMS, and support services by Cloudare Technologies."
        />
        <link rel="canonical" href={siteUrl} />
      </Helmet>

      <section className={zohoStyles.hero}>
        <div className={zohoStyles.containerGrid}>
          <div className={zohoStyles.headContainer}>
            <img
              src={OracleLogo}
              alt="Oracle EBS Logo"
              className={zohoStyles.heroLogo}
            />
            <h1 className={zohoStyles.heroTitle1}>
              Oracle E-Business Suite Implementation & Support
            </h1>
            <p className={zohoStyles.heroDesc}>
              End-to-end Oracle EBS consulting including implementation,
              upgrades, integrations, GST localization, and AMS services for
              enterprises across India and global markets.
            </p>

            <div className={zohoStyles.heroButtons}>
              <a href={calendly} className={zohoStyles.btnPrimary}>
                Book a Consultation <ArrowRight className={zohoStyles.iconSm} />
              </a>
              <a href="#faq" className={zohoStyles.btnSecondary}>
                View FAQs <ArrowRight className={zohoStyles.iconSm} />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Why Choose Cloudare for Oracle EBS
          </h2>

          <div className={zohoStyles.featureGrid}>
            <FeatureCard
              icon={<Building2 className={zohoStyles.iconLg} />}
              title="Enterprise Expertise"
              desc="Deep experience across Finance, SCM, Manufacturing, and HRMS modules."
            />
            <FeatureCard
              icon={<Shield className={zohoStyles.iconLg} />}
              title="Compliance & Security"
              desc="India localization, GST, audit controls, and secure role-based access."
            />
            <FeatureCard
              icon={<PlugZap className={zohoStyles.iconLg} />}
              title="Integrations & Automation"
              desc="Seamless integrations with banks, tax portals, BI tools, and cloud platforms."
            />
          </div>
        </div>
      </section>

      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Oracle EBS Services We Offer
          </h2>

          <div className={zohoStyles.appGrid}>
            <AppCard
              title="Oracle Financials"
              desc="GL, AP, AR, FA, CM, budgeting and financial reporting."
              icon={<PieChart className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="SCM & Manufacturing"
              desc="Inventory, BOM, WIP, Purchasing, Order Management."
              icon={<Package className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="HRMS"
              desc="Payroll, OTL, SSHR, and employee lifecycle management."
              icon={<Users2 className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Oracle EBS Upgrades"
              desc="R11i / R12 to R12.2 upgrades with minimal downtime."
              icon={<Shield className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="Integrations"
              desc="OIC, SOA, APIs, DB links, third-party integrations."
              icon={<Network className={zohoStyles.iconLg} />}
            />
            <AppCard
              title="AMS & Support"
              desc="L2/L3 support, monitoring, patching, and enhancements."
              icon={<PlugZap className={zohoStyles.iconLg} />}
            />
          </div>
        </div>
      </section>

      <section className={zohoStyles.sectionAlt}>
        <div className={zohoStyles.container}>
          <h2 className={zohoStyles.sectionTitle}>
            Oracle EBS Success Stories
          </h2>

          <div className={zohoStyles.caseGrid}>
            <CaseStudy
              logo={Client1}
              title="Sugna Foods — Oracle Financials"
              bullets={[
                "GST localization & compliance",
                "Month-end close automation",
              ]}
            />
            <CaseStudy
              logo={Client2}
              title="Integra Life — SCM Optimization"
              bullets={["Inventory optimization", "Order-to-cash automation"]}
            />
          </div>

          <div className={zohoStyles.statGrid}>
            <StatCard
              label="Oracle EBS Projects"
              value="20+"
              maxProgress={80}
            />
            <StatCard
              label="Upgrade Success Rate"
              value="100%"
              maxProgress={90}
            />
            <StatCard
              label="Avg Go-Live Time"
              value="6-8 weeks"
              maxProgress={70}
            />
          </div>
        </div>
      </section>
      <section className={zohoStyles.section}>
        <div className={zohoStyles.container}>
          <div className={zohoStyles.contactCard}>
            <div className={zohoStyles.contact1}>
              <h2 className={zohoStyles.sectionTitle}>
                Talk to an Oracle EBS Expert
              </h2>
              <p className={zohoStyles.heroDesc}>
                Book a free 30-minute consultation.
              </p>

              <div className={zohoStyles.heroButtons}>
                <a
                  href={calendly}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={zohoStyles.btnPrimary}
                >
                  Book on Calendly <ArrowRight className={zohoStyles.iconSm} />
                </a>
                <a href={`mailto:${email}`} className={zohoStyles.btnSecondary}>
                  <Mail className={zohoStyles.iconSm} /> {email}
                </a>
              </div>
            </div>

            <div className={zohoStyles.contactInfo}>
              <ContactRow
                label="Phone"
                value={phoneDisplay}
                href={`tel:${phoneHref}`}
                icon={<Phone className={zohoStyles.iconSm} />}
              />
              {/* <ContactRow
                label="Email"
                value={email}
                href={`mailto:${email}`}
                icon={<Mail className={zohoStyles.iconSm} />}
              /> */}
              {/* <div className={zohoStyles.locationText}>
                📍 India · KSA · US · Canada
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <div id="faq">
        <FaqSection
          title="Oracle EBS — FAQ"
          faqs={faqList.map((x) => ({ question: x.q, answer: x.a }))}
          emitJsonLd
        />
      </div>

      <Footer />
    </main>
  );
}

function FeatureCard({ icon, title, desc }) {
  return (
    <div className={zohoStyles.featureCard}>
      <div className={zohoStyles.featureHeader}>
        <div className={zohoStyles.featureIcon}>{icon}</div>
        <h3 className={zohoStyles.featureTitle}>{title}</h3>
      </div>
      <p className={zohoStyles.featureDesc}>{desc}</p>
    </div>
  );
}

function AppCard({ title, desc, icon }) {
  return (
    <div className={zohoStyles.appCard}>
      <div className={zohoStyles.appHeader}>
        <div className={zohoStyles.appIcon}>{icon}</div>
        <h3 className={zohoStyles.appTitle}>{title}</h3>
      </div>
      <p className={zohoStyles.appDesc}>{desc}</p>
    </div>
  );
}

function CaseStudy({ logo, title, bullets }) {
  return (
    <div className={zohoStyles.caseCard}>
      <div className={zohoStyles.caseImage}>
        <img src={logo} alt={title} className={zohoStyles.caseLogo} />
      </div>
      <h3 className={zohoStyles.caseTitle}>{title}</h3>
      <ul className={zohoStyles.caseList}>
        {bullets.map((b, i) => (
          <li key={i}>{b}</li>
        ))}
      </ul>
    </div>
  );
}

function StatCard({ label, value, maxProgress }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let start = 0;
    const interval = setInterval(() => {
      start += 2;
      if (start >= maxProgress) {
        start = maxProgress;
        clearInterval(interval);
      }
      setProgress(start);
    }, 20);
    return () => clearInterval(interval);
  }, [maxProgress]);

  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <div className={zohoStyles.statCard}>
      <svg className={zohoStyles.circleChart} width="100" height="100">
        <circle
          className={zohoStyles.circleBg}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
        />
        <circle
          className={zohoStyles.circleProgress}
          cx="50"
          cy="50"
          r={radius}
          strokeWidth="10"
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
        />
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="middle"
          className="statValue"
        >
          {value}
        </text>
      </svg>
      <div className="statLabel">{label}</div>
    </div>
  );
}

function ContactRow({ label, value, href, icon }) {
  return (
    <div className={zohoStyles.contactRow}>
      <div className={zohoStyles.contactIcon}>{icon}</div>
      <div>
        <div className={zohoStyles.contactLabel}>{label}</div>
        <a href={href} className={zohoStyles.contactValue}>
          {value}
        </a>
      </div>
    </div>
  );
}
